import React, { useState, useEffect, useRef } from 'react';

import axios from 'axios';

import SchoolAvatar from './SchoolAvatar';

import { getApiBaseUrl } from '../apiBase';

import {

    getActiveSchool,

    setActiveSchool,

    getActiveSchoolId

} from '../utils/activeSchool';

import { fetchDemoSchools, disableDemoSchool } from '../demo/demoApi';
import { markFreelanceCoach } from '../utils/roles';
import { useTranslation } from 'react-i18next';




const ROLE_I18N_KEYS = {
    owner: 'roleOwner',
    coach: 'roleCoach',
    member: 'roleMember',
    staff: 'roleStaff',
    demo: 'roleDemo'
};

export default function ActiveSchoolSwitcher({ compact = false }) {
    const { t } = useTranslation(['schoolSwitcher', 'common', 'nav', 'messages', 'errors']);

    const [schools, setSchools] = useState([]);

    const [demoSchools, setDemoSchools] = useState([]);

    const [open, setOpen] = useState(false);

    const [active, setActive] = useState(getActiveSchool);

    const wrapRef = useRef(null);

    const token = localStorage.getItem('token');



    useEffect(() => {

        if (!token) return;

        loadSchools();

        const onChanged = () => setActive(getActiveSchool());

        window.addEventListener('active-school-changed', onChanged);

        window.addEventListener('member-school-changed', onChanged);

        return () => {

            window.removeEventListener('active-school-changed', onChanged);

            window.removeEventListener('member-school-changed', onChanged);

        };

    }, [token]);



    useEffect(() => {

        if (!open) return;

        const onDoc = (e) => {

            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);

        };

        document.addEventListener('mousedown', onDoc);

        return () => document.removeEventListener('mousedown', onDoc);

    }, [open]);



    async function loadSchools() {

        try {

            const api = axios.create({

                baseURL: getApiBaseUrl(),

                headers: { Authorization: `Bearer ${token}` }

            });

            const [res, demo] = await Promise.all([

                api.get('/school/available'),

                fetchDemoSchools().catch(() => [])

            ]);

            const list = res.data || [];

            const demoList = Array.isArray(demo) ? demo : [];

            setSchools(list.filter(s => s.accessRole !== 'demo'));

            setDemoSchools(demoList);

            if (list.some(s => s.isFreelanceSchool)) markFreelanceCoach();

            const cur = getActiveSchool();

            if (list.length === 1 && !cur) {

                selectSchool(list[0], false);

            } else if (cur) {

                const inList = list.some(s => s.schoolId === cur.schoolId);

                const inDemo = demoList.some(d => d.id === cur.schoolId);

                if (!inList && !inDemo && !(cur.isDemoSchool || cur.accessRole === 'demo')) {

                    setActiveSchool(null);

                    setActive(null);

                }

            }

        } catch {

            /* ignore */

        }

    }



    const ACCESS_TO_NAV_ROLE = { owner: 'Owner', coach: 'Coach', member: 'Member', staff: 'Staff', demo: 'Owner' };



    function selectSchool(school, reload = true) {

        const entry = {

            schoolId: school.schoolId ?? school.id,

            schoolName: school.schoolName ?? school.name,

            schoolLogo: school.schoolLogo ?? school.logo,

            accessRole: school.accessRole,

            studentId: school.studentId ?? null,

            isDemoSchool: school.isDemoSchool || school.accessRole === 'demo',
            isFreelanceSchool: school.isFreelanceSchool || false

        };

        setActiveSchool(entry);

        const navRole = ACCESS_TO_NAV_ROLE[school.accessRole];

        if (navRole) localStorage.setItem('role', navRole);
        if (school.isFreelanceSchool) markFreelanceCoach();

        setActive(entry);

        setOpen(false);

        window.dispatchEvent(new CustomEvent('active-school-changed', { detail: { school: entry } }));

        window.dispatchEvent(new CustomEvent('member-school-changed', {

            detail: { schoolId: entry.schoolId, profile: { ...school, id: school.studentId } }

        }));

        if (school.accessRole === 'demo' || school.isDemoSchool) {

            window.dispatchEvent(new CustomEvent('demo-school-selected', { detail: entry }));

        }

        if (reload) window.location.reload();

    }



    async function handleDisableDemo(demoId, e) {

        e.stopPropagation();

        await disableDemoSchool();

        await loadSchools();

        const cur = getActiveSchool();

        if (cur?.schoolId === demoId) {

            const owned = schools[0];

            if (owned) selectSchool(owned);

        }

    }



    const realSchools = schools;

    const hasDemo = demoSchools.length > 0;

    if (!token || (realSchools.length === 0 && !hasDemo)) return null;

    if (realSchools.length === 1 && !hasDemo && !compact) return null;



    const current = active || [...realSchools, ...demoSchools.map(d => ({

        schoolId: d.id,

        schoolName: d.name,

        accessRole: 'demo',

        isDemoSchool: true

    }))].find(s => s.schoolId === getActiveSchoolId()) || realSchools[0];

    const avatarSize = compact ? 32 : 40;



    return (

        <div

            ref={wrapRef}

            className="sp-school-switcher"

            style={{

                display: 'flex',

                alignItems: 'center',

                gap: '8px',

                ...(compact ? {} : { marginBottom: '16px' })

            }}

        >

            {!compact && (

                <span style={{ fontSize: '13px', color: '#555', fontWeight: '600' }}>{t('schoolSwitcher:schoolLabel')}</span>

            )}



            <button

                type="button"

                onClick={() => setOpen(v => !v)}

                title={current?.schoolName}

                style={{

                    display: 'flex', alignItems: 'center', gap: '8px',

                    padding: compact ? '4px 10px 4px 4px' : '6px 14px 6px 6px',

                    borderRadius: '999px',

                    border: compact ? '1px solid rgba(255,255,255,0.25)' : '1px solid #ddd',

                    background: compact ? 'rgba(255,255,255,0.1)' : 'white',

                    color: compact ? 'white' : '#1A1A1A',

                    cursor: 'pointer',

                    fontSize: compact ? '12px' : '14px',

                    fontWeight: '600',

                    maxWidth: compact ? '220px' : '360px'

                }}

            >

                <SchoolAvatar logoPath={current?.schoolLogo} size={avatarSize} alt={current?.schoolName} />

                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>

                    {current?.isDemoSchool
                        ? `👁 ${current?.schoolName || t('schoolSwitcher:demoFallback')}`
                        : (current?.schoolName || t('schoolSwitcher:schoolFallback'))}

                </span>

                <span style={{ opacity: 0.6, fontSize: '10px' }}>▼</span>

            </button>



            {open && (

                <div

                    className={`sp-school-switcher__menu${compact ? '' : ' sp-school-switcher__menu--align-left'}`}

                    role="listbox"

                >

                    {realSchools.length > 0 && (

                        <div style={{ padding: '8px 14px 4px', fontSize: '11px', fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>

                            {t('schoolSwitcher:mySchools')}

                        </div>

                    )}

                    {realSchools.map(s => (

                        <SchoolRow key={`${s.schoolId}-${s.accessRole}`} school={s} current={current} onSelect={selectSchool} />

                    ))}



                    {hasDemo && (

                        <>

                            <div style={{ padding: '10px 14px 4px', fontSize: '11px', fontWeight: 700, color: '#888', textTransform: 'uppercase', borderTop: '1px solid #f0f0f0' }}>

                                {t('schoolSwitcher:demoSchools')}

                            </div>

                            {demoSchools.map(d => (

                                <div key={d.id} style={{ display: 'flex', alignItems: 'stretch' }}>

                                    <button

                                        type="button"

                                        onClick={() => selectSchool({

                                            schoolId: d.id,

                                            schoolName: d.name,

                                            accessRole: 'demo',

                                            isDemoSchool: true,

                                            sportTypeIcon: d.sportEmoji

                                        })}

                                        style={{

                                            flex: 1,

                                            display: 'flex',

                                            alignItems: 'center',

                                            gap: '12px',

                                            padding: '12px 14px',

                                            border: 'none',

                                            borderBottom: '1px solid #f0f0f0',

                                            background: d.id === current?.schoolId ? '#f3eeff' : 'white',

                                            cursor: 'pointer',

                                            textAlign: 'left'

                                        }}

                                    >

                                        <span style={{ fontSize: 22 }}>{d.sportEmoji || '👁'}</span>

                                        <div>

                                            <div style={{ fontWeight: '600', fontSize: '14px', color: '#1A1A1A' }}>

                                                {d.name}

                                            </div>

                                            <div style={{ fontSize: '11px', color: '#888' }}>

                                                {d.city} · {t('schoolSwitcher:viewOnly')}

                                            </div>

                                        </div>

                                    </button>

                                    <button

                                        type="button"

                                        title={t('schoolSwitcher:disable')}

                                        onClick={(e) => handleDisableDemo(d.id, e)}

                                        style={{

                                            border: 'none',

                                            borderBottom: '1px solid #f0f0f0',

                                            background: '#fafafa',

                                            color: '#c62828',

                                            fontSize: '11px',

                                            padding: '0 10px',

                                            cursor: 'pointer'

                                        }}

                                    >

                                        ×

                                    </button>

                                </div>

                            ))}

                        </>

                    )}



                    <a

                        href="/profile#demo-schools"

                        onClick={() => setOpen(false)}

                        style={{

                            display: 'block', padding: '10px 14px', fontSize: '12px',

                            color: '#EB3333', textAlign: 'center', textDecoration: 'none',

                            background: '#fafafa'

                        }}

                    >

                        {t('schoolSwitcher:enableMoreDemo')}

                    </a>

                    <a

                        href="/select-school"

                        onClick={() => setOpen(false)}

                        style={{

                            display: 'block', padding: '10px 14px', fontSize: '12px',

                            color: '#EB3333', textAlign: 'center', textDecoration: 'none',

                            background: '#fafafa',

                            borderTop: '1px solid #eee'

                        }}

                    >

                        {t('schoolSwitcher:allSchools')}

                    </a>

                </div>

            )}

        </div>

    );

}



function SchoolRow({ school, current, onSelect }) {
    const { t } = useTranslation(['schoolSwitcher']);
    const roleKey = ROLE_I18N_KEYS[school.accessRole];
    const roleLabel = roleKey ? t(`schoolSwitcher:${roleKey}`) : school.accessRole;

    return (

        <button

            type="button"

            onClick={() => onSelect(school)}

            style={{

                width: '100%',

                display: 'flex',

                alignItems: 'center',

                gap: '12px',

                padding: '12px 14px',

                border: 'none',

                borderBottom: '1px solid #f0f0f0',

                background: school.schoolId === current?.schoolId ? '#f3eeff' : 'white',

                cursor: 'pointer',

                textAlign: 'left'

            }}

        >

            <SchoolAvatar logoPath={school.schoolLogo} size={36} alt={school.schoolName} />

            <div>

                <div style={{ fontWeight: '600', fontSize: '14px', color: '#1A1A1A' }}>

                    {school.schoolName}

                </div>

                <div style={{ fontSize: '11px', color: '#888' }}>

                    {roleLabel}
                    {school.studentStatus === 'archived' ? t('schoolSwitcher:inArchive') : ''}

                </div>

            </div>

        </button>

    );

}

